/* eslint-disable react/jsx-no-literals */
/* eslint-disable no-magic-numbers */
import React from 'react';
import Typography from 'carbon-react/lib/components/typography';
import Box from 'carbon-react/lib/components/box';
import Message from 'carbon-react/lib/components/message';
import Tooltip from 'carbon-react/lib/components/tooltip';

import * as styles from './cost-structure-card.scss';

// ─── Types ────────────────────────────────────────────────────────────────────
export interface CostSegment {
  label: string;
  value: number;
  color: string;
}

export interface CostStructureMessage {
  title: string;
  content: string;
  variant: 'warning' | 'error' | 'info' | 'success';
}

export interface CostStructureCardProps {
  title: string;
  subtitle?: string;
  totalLabel: string;
  segments: CostSegment[];
  formatValue?: (value: number) => string;
  formatTotal?: (value: number) => string;
  message?: CostStructureMessage;
}

// ─── Fallback formatter (used when mapper doesn't provide currency-aware ones) ─
const defaultFormatTotal = (value: number) => `$${value.toLocaleString()}`;

// ─── Component ────────────────────────────────────────────────────────────────
export const CostStructureCard = ({
  title,
  subtitle,
  totalLabel,
  segments,
  formatTotal = defaultFormatTotal,
  message
}: CostStructureCardProps) => {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const titleMb = subtitle ? 0 : 2;

  return (
    <div className={styles.cardContainer}>

      <Typography fontSize="21px" fontWeight="500" lineHeight="125%" mb={titleMb}>
        {title}
      </Typography>
      {subtitle && (
        <Typography fontSize="14px" fontWeight="400" lineHeight="150%" mb={2} color="var(--colorsUtilityYin055)">
          {subtitle}
        </Typography>
      )}

      {/* Total value */}
      <Box display="flex" alignItems="baseline" gap="8px" mb={3}>
        <Typography fontSize="32px" fontWeight="900" lineHeight="120%" mb={0}>
          {formatTotal(total)}
        </Typography>
        <Typography fontSize="14px" fontWeight="400" lineHeight="140%" mb={0} color="var(--colorsUtilityYin055)">
          {totalLabel}
        </Typography>
      </Box>

      {/* Stacked bar */}
      <div className={styles.stackedBar}>
        {segments.map(seg => (
          <div
            key={seg.label}
            className={styles.barSegment}
            style={{ width: `${(seg.value / total) * 100}%`, backgroundColor: seg.color }}
          >
            <Tooltip message={seg.label} position="bottom">
              <span className={styles.barSegmentLabel}>{seg.label}</span>
            </Tooltip>
          </div>
        ))}
      </div>

      {/* Legend */}
      <Box mt={2} mb={0} flex="1">
        <div className={styles.legendRow}>
          {segments.map(seg => (
            <div key={seg.label} className={styles.legendItem}>
              <span className={styles.legendDot} style={{ backgroundColor: seg.color }} />
              <div className={styles.legendText}>
                <Typography fontSize="14px" fontWeight="500" lineHeight="150%" mb={0} color="rgba(0,0,0,0.90)">
                  {`${seg.label}:`}
                </Typography>
                <Typography fontSize="14px" fontWeight="400" lineHeight="150%" mb={0} color="rgba(0,0,0,0.65)">
                  {formatTotal(seg.value)}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      </Box>

      {/* Optional status message */}
      {message && (
        <>
          <Box my={2} backgroundColor="var(--colorsUtilityMajor100)" height="1px" as="hr" />
          <Message title={message.title} variant={message.variant}>
            {message.content}
          </Message>
        </>
      )}

    </div>
  );
};
